import React, {Dispatch, useEffect, useMemo} from "react"
import {useAppDispatch, useAppSelector} from "../../../../hooks/redux"
import Modal from "../Modal"
import classes from "../Modal/Modal.module.scss"
import {useFormik} from "formik"
import Input from "../../../generic/dataEntry/Input/Input"
import {
  addEmployee,
  fetchEmployees, setCurrentPage,
  updateEmployee
} from "../../../../store/actions/employeeActions"
import {employeeSlice} from "../../../../store/slices/employeeSlice"
import CustomSelect from "../../../generic/dataEntry/CustomSelect/CustomSelect"
import {CustomSelectOption} from "../../../../models"
import {IEmployee} from "../../../../models/employee"
import {fetchBrigades} from "../../../../store/actions/brigadeActions"

interface IAddEmployeeModalProps {
  close: any
  children?: string | JSX.Element | JSX.Element[]
  editSuccess: Dispatch<any>
  editableItem?: IEmployee
}

export default function ModalEditEmployee({close, editSuccess, editableItem}: IAddEmployeeModalProps) {

  const dispatch = useAppDispatch()
  const {error, pageSize, isUpdating, isCreating, currentPage} = useAppSelector(state => state.employee)
  const brigades = useAppSelector(state => state.brigade.list)
  const {employeeRoles} = useAppSelector(state => state.handbook)
  const modalTitle = editableItem ? "Редактировать сотрудника" : "Добавить сотрудника"

  useEffect(() => {
    dispatch(fetchBrigades({page: 1, pageSize: 0}))
  }, [dispatch])

  const initialFormValues =
    editableItem ?
      {
        user_uuid: editableItem.user_uuid,
        fio: editableItem.fio,
        email: editableItem.email,
        phone: editableItem.phone,
        password: "",
        role_id: String(editableItem.role_id),
        brigade_id: editableItem.brigade_id
      } : {
        fio: "",
        email: "",
        phone: "",
        password: "",
        role_id: "",
        brigade_id: ""
      }

  const {values, touched, handleChange, handleReset} = useFormik({
    initialValues: initialFormValues,
    onSubmit: () => {
    }
  })

  const saveHandler = async () => {
    const employeeID = await dispatch(addEmployee(values))

    if (employeeID) {
      dispatch(setCurrentPage(1))
      await dispatch(fetchEmployees({page: 1, pageSize}))
      close()
      editSuccess(true)
      handleReset(initialFormValues)
    }
  }

  const updateHandler = async () => {
    const isUpdated = await dispatch(updateEmployee(values))

    if (isUpdated) {
      editSuccess(true)
      dispatch(setCurrentPage(1))
      await dispatch(fetchEmployees({page: 1, pageSize}))
      close()
      handleReset(initialFormValues)
    }
  }

  const handleFieldChange = (e: any) => {
    handleChange(e)
    const fieldName: any = e.target.getAttribute('name')

    if (error[fieldName]) {
      dispatch(employeeSlice.actions.clearFieldError(fieldName))
    }
  }

  const handleSelectChange = (e: CustomSelectOption, name: string) => {
    handleChange({target: {name, value: e.value}})

    if (name === "role_id") {
      handleChange({target: {name: "brigade_id", value: ""}})
    }

    if (error[name]) {
      dispatch(employeeSlice.actions.clearFieldError(name))
    }
  }

  const brigadeOptions = useMemo(
    () => {
      const list = brigades.map(item => (
        {
          label: item.brigade,
          value: String(item.id)
        }
      ))

      list.unshift({label: "Не выбрана", value: ""})
      return list

  }, [brigades])

  const roleOptions = useMemo(
    () => {
      const opt = [{
        label: "Не выбрана",
        value: ""
      }]
      for (let [key, value] of Object.entries(employeeRoles)) {
        opt.push({value: key, label: value})
      }
      return opt
    },
    [employeeRoles]
  )

  const closeHandler = () => {
    close()
    dispatch(employeeSlice.actions.clearAllErrors())
  }

  return (
    <Modal
      title={modalTitle}
      close={closeHandler}
      dialogSizeSuffix={"w435"}
      submitClick={editableItem ? updateHandler : saveHandler}
      cancelClick={closeHandler}
      submitDisabled={isUpdating || isCreating}
      submitLoading={isUpdating || isCreating}
    >
      <form
        className={classes.form}
      >
        <div className={classes.form_field}>
          <Input
            value={values.fio}
            onChange={(e: React.ChangeEvent) => {
              handleFieldChange(e)
            }}
            name={"fio"}
            label={"Полное имя (ФИО)"}
            valid={!error.fio}
            shouldValidate
            errorMessage={error.fio}
            touched={touched.fio}
            required
          />
        </div>
        <div className={classes.form_field}>
          <Input
            value={values.email}
            onChange={(e: React.ChangeEvent) => {
              handleFieldChange(e)
            }}
            name={"email"}
            label={"E-mail"}
            valid={!error.email}
            shouldValidate
            errorMessage={error.email}
            touched={touched.email}
            required
          />
        </div>
        <div className={classes.form_field}>
          <Input
            mask="+7 (999) 999-99-99"
            value={values.phone}
            onChange={(e: React.ChangeEvent) => {
              handleFieldChange(e)
            }}
            name={"phone"}
            label={"Номер телефона"}
            valid={!error.phone}
            shouldValidate
            errorMessage={error.phone}
            touched={touched.phone}
            required
          />
        </div>
        <div className={classes.form_field}>
          <Input
            type={"password"}
            value={values.password}
            onChange={(e: React.ChangeEvent) => {
              handleFieldChange(e)
            }}
            name={"password"}
            label={editableItem ? "Новый пароль" : "Пароль"}
            valid={!error.password}
            errorMessage={error.password}
            shouldValidate={!editableItem}
            touched={touched.password}
            required={!editableItem}
            passwordNote={editableItem ? "Укажите новый пароль, если хотите его поменять" : ""}
            autoComplete={'new-password'}
          />
        </div>
        <div className={classes.form_field}>
          <CustomSelect
            onChange={(e: CustomSelectOption) => {
              handleSelectChange(e, "role_id")
            }}
            label={"Роль"}
            valid={!error.role_id}
            shouldValidate
            value={roleOptions.find(elem => elem.value === String(values.role_id))}
            options={roleOptions}
            name={"role_id"}
            errorMessage={error.role_id}
            isSearchable
            required
            placeholder={"Не выбрана"}
          />
        </div>
        <div className={classes.form_field}>
          <CustomSelect
            onChange={(e: CustomSelectOption) => {
              handleSelectChange(e, "brigade_id")
            }}
            label={"Бригада"}
            valid={!error.brigade_id}
            value={brigadeOptions.find(elem => elem.value === String(values.brigade_id))}
            options={brigadeOptions}
            name={"brigade_id"}
            errorMessage={error.brigade_id}
            isSearchable
            placeholder={"Не выбрана"}
            disabled={values.role_id !== "3"}
          />
        </div>
      </form>
    </Modal>
  )
}